/** Pure-SVG hexagonal radar chart for 6 dimensions. */

interface RadarValue {
	label: string;
	score: number;
}

interface RadarProps {
	values: RadarValue[];
	max?: number;
	size?: number;
	color?: string;
}

export function Radar({
	values,
	max = 10,
	size = 160,
	color = "var(--color-primary)",
}: RadarProps) {
	const cx = size / 2;
	const cy = size / 2;
	const r = size / 2 - 18;
	const N = values.length;

	const pt = (i: number, v: number): [number, number] => {
		const a = (i / N) * Math.PI * 2 - Math.PI / 2;
		const rr = (v / max) * r;
		return [cx + Math.cos(a) * rr, cy + Math.sin(a) * rr];
	};

	const polyPoints = values.map((v, i) => pt(i, v.score).join(",")).join(" ");

	const labelPt = (i: number): [number, number] => {
		const a = (i / N) * Math.PI * 2 - Math.PI / 2;
		return [cx + Math.cos(a) * (r + 12), cy + Math.sin(a) * (r + 12)];
	};

	const rings = [0.25, 0.5, 0.75, 1] as const;

	return (
		<svg viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size }} aria-hidden="true">
			{rings.map((m) => (
				<polygon
					key={m}
					points={values.map((_, j) => pt(j, max * m).join(",")).join(" ")}
					fill="none"
					stroke="var(--color-hairline)"
					strokeWidth={1}
				/>
			))}
			{values.map((val, i) => {
				const [x2, y2] = pt(i, max);
				return (
					<line
						key={val.label}
						x1={cx}
						y1={cy}
						x2={x2}
						y2={y2}
						stroke="var(--color-hairline)"
						strokeWidth={1}
					/>
				);
			})}
			<polygon
				points={polyPoints}
				fill={color}
				fillOpacity="0.18"
				stroke={color}
				strokeWidth={1.5}
			/>
			{values.map((v, i) => {
				const [x, y] = pt(i, v.score);
				return <circle key={v.label} cx={x} cy={y} r={2.5} fill={color} />;
			})}
			{values.map((v, i) => {
				const [x, y] = labelPt(i);
				return (
					<text
						key={v.label}
						x={x}
						y={y}
						textAnchor="middle"
						dominantBaseline="middle"
						fontSize="9"
						fill="var(--color-ink-subtle)"
						letterSpacing="0.06em"
						style={{ textTransform: "uppercase" }}
					>
						{v.label}
					</text>
				);
			})}
		</svg>
	);
}
